import { useCurrentUserRole } from "@/features/auth/hooks/useCurrentUserRole";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { AppRoutes } from "@/routes/config";
import { TUserRole } from "@/types/enum";
import { FCC } from "@/types/common";
import { Navigate } from "react-router-dom";

type TProps = {
  /** Roles allowed to access the wrapped route. Others get a 404. */
  allow: TUserRole[];
};

/**
 * Route-level role guard. Menu hiding alone doesn't stop a user typing the URL directly,
 * so this blocks disallowed roles at the route. Per product decision, an unauthorized role
 * sees a 404 (not a dedicated 403 page) — the screen simply "doesn't exist" for them.
 *
 * Redirects to the standalone /page-not-found route (which lives OUTSIDE the app layout) so
 * the 404 renders full-screen without the header/menu — rendering <PageNotFound/> inline here
 * would leave it nested inside CenterLayout and still show the menu.
 *
 * Waits for /get-me to resolve before deciding, so we never flash a 404 while the role is
 * still loading.
 */
export const RequireRole: FCC<TProps> = ({ allow, children }) => {
  const { role, isLoading } = useCurrentUserRole();

  if (isLoading) return <LoadingArea height={400} />;
  if (!role || !allow.includes(role)) return <Navigate to={AppRoutes.pageNotFound} replace />;

  return <>{children}</>;
};
